import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Scissors, Check, X, CircleDot } from "lucide-react";
import api from "../lib/api";

const STATUS = ["pending", "ready", "delivered"];
const STATUS_COLOR = {
  pending: "bg-amber-500/20 text-amber-400",
  ready: "bg-blue-500/20 text-blue-400",
  delivered: "bg-emerald-500/20 text-emerald-400",
};

export default function PosAlterations() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", description: "", due_date: "", bill_id: "", status: "pending" });
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/pos/alterations").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.customer_name || !form.customer_phone || !form.description) return toast.error("Name, phone and description required");
    try { await api.post("/pos/alterations", form); toast.success("Job created"); setForm({ customer_name: "", customer_phone: "", description: "", due_date: "", bill_id: "", status: "pending" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (job, newStatus) => {
    await api.put(`/pos/alterations/${job.id}`, { ...job, status: newStatus });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Remove this job?")) return;
    await api.delete(`/pos/alterations/${id}`);
    load();
  };

  const filtered = filter === "all" ? list : list.filter((j) => j.status === filter);

  return (
    <div className="p-8" data-testid="pos-alterations-page">
      <div className="flex items-center gap-3 mb-1">
        <Scissors className="text-amber-500" size={22} />
        <h1 className="text-2xl font-semibold text-slate-100">Alterations &amp; Stitching</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Track pending, ready and delivered jobs. Ping customers when a piece is ready for pickup.</p>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div>
          <div className="flex gap-2 mb-4">
            {["all", ...STATUS].map((s) => (
              <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs rounded uppercase tracking-widest ${filter === s ? "bg-amber-500 text-slate-950 font-medium" : "bg-slate-900 border border-slate-800 text-slate-400"}`}>{s}</button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="bg-slate-900 border border-dashed border-slate-800 rounded-md py-16 text-center text-slate-600">No jobs</div>
          ) : (
            <div className="space-y-3">
              {filtered.map((j) => (
                <div key={j.id} className="bg-slate-900 border border-slate-800 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <CircleDot size={16} className="text-amber-500 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-slate-100 font-medium">{j.customer_name}</p>
                        <span className="text-xs text-slate-500 font-mono">{j.customer_phone}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_COLOR[j.status]}`}>{j.status}</span>
                      </div>
                      <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{j.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        {j.due_date && <span>Due: {j.due_date}</span>}
                        {j.bill_id && <span>Bill: {j.bill_id}</span>}
                        <span>Created: {new Date(j.created_at).toLocaleDateString("en-IN")}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <select value={j.status} onChange={(e) => setStatus(j, e.target.value)} className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 uppercase font-mono">
                        {STATUS.map((s) => <option key={s}>{s}</option>)}
                      </select>
                      <button onClick={() => del(j.id)} className="text-xs text-slate-500 hover:text-red-400">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="bg-slate-900 border border-slate-800 rounded-md p-5 h-fit sticky top-6">
          <h3 className="text-sm font-medium text-slate-200 mb-4 flex items-center gap-2"><Plus size={14} /> New job</h3>
          <div className="space-y-3">
            <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer name" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200" />
            <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="Phone" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 font-mono" />
            <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Alteration details (e.g. blouse fitting, hem shortening, sleeves…)" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 resize-none" />
            <input value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} placeholder="Due date (e.g. 25 Feb)" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200" />
            <input value={form.bill_id} onChange={(e) => setForm({ ...form, bill_id: e.target.value })} placeholder="Related bill no (optional)" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 font-mono" />
            <button onClick={save} className="w-full py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-medium text-sm">Create job</button>
          </div>
        </aside>
      </div>
    </div>
  );
}
